function _slicedToArray(r, e) { return _arrayWithHoles(r) || _iterableToArrayLimit(r, e) || _unsupportedIterableToArray(r, e) || _nonIterableRest(); }
function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
function _iterableToArrayLimit(r, l) { var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (null != t) { var e, n, i, u, a = [], f = !0, o = !1; try { if (i = (t = t.call(r)).next, 0 === l) { if (Object(t) !== t) return; f = !1; } else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0); } catch (r) { o = !0, n = r; } finally { try { if (!f && null != t.return && (u = t.return(), Object(u) !== u)) return; } finally { if (o) throw n; } } return a; } }
function _arrayWithHoles(r) { if (Array.isArray(r)) return r; }
import React, { useState, useRef, useEffect } from 'react';
import classNames from 'classnames';
import "./AutoResizeTextarea.css";
import { jsx as _jsx } from "react/jsx-runtime";
function implode(input) {
  return input.split('\n').map(string => string.trim()).filter(string => string) // remove empty strings (e.g. from trailing newlines)
  .join(' ');
}

/**
 * Splits a string on a delimiter, respecting quoted spans.
 * Delimiters that appear inside `"..."` or `'...'` are treated as literal
 * characters and do not cause a split. Quotes are preserved in the returned
 * tokens so the operation is lossless (suitable for display-only transformations
 * where the original text must be recoverable).
 *
 * An unbalanced opening quote (no matching closing quote found ahead) is treated
 * as a literal character and splitting proceeds normally.
 *
 * @param input The string to split.
 * @param delimiter The delimiter to split on.
 * @returns An array of tokens. Always contains at least one element.
 */
function splitOnDelimiter(input, delimiter) {
  // Walk the string character by character, tracking quoted spans so that
  // delimiters inside "..." or '...' are not treated as split points.
  // Quotes are preserved in the output (not stripped) so the round-trip is lossless.
  // An unbalanced quote is treated as a literal character — splitting continues normally.
  var tokens = [];
  var current = '';
  var quoteChar = null;
  var i = 0;
  while (i < input.length) {
    var ch = input[i];
    if (quoteChar !== null) {
      // Inside a quoted span — accumulate until we hit the matching closing quote
      current += ch;
      if (ch === quoteChar) {
        quoteChar = null;
      }
      i += 1;
    } else if (ch === '"' || ch === "'") {
      // Only treat as a quoted span if there is a matching closing quote ahead.
      // An unbalanced quote is treated as a literal character so the delimiter
      // logic continues to work normally.
      var closingIdx = input.indexOf(ch, i + 1);
      if (closingIdx !== -1) {
        quoteChar = ch;
      }
      current += ch;
      i += 1;
    } else if (input.startsWith(delimiter, i)) {
      // Delimiter found outside a quoted span — emit the current token and advance
      tokens.push(current);
      current = '';
      i += delimiter.length;
    } else {
      // Plain character outside any quoted span and not part of the delimiter — accumulate it
      current += ch;
      i += 1;
    }
  }
  tokens.push(current);
  return tokens;
}
function explode(input, delimiter) {
  // Split by delimiter (commonly " " or " -") respecting quoted spans, then
  // join with newline + trimmed delimiter so each token appears on its own line.
  // Empty tokens (e.g. from multiple consecutive delimiters) are filtered out.
  return splitOnDelimiter(input.trim(), delimiter).filter(token => token !== '') // remove empty strings
  .join("\n".concat(delimiter.trim()));
}

/**
 * Makes a textarea that auto resizes based on contents, its height grows with new lines.
 * If a delimiter is set, such as " -" or " ", as used by jvm args or env vars
 * then the field will also "explode" the value by the delimiter over new lines
 * on focus, and implode on blur. By default, it doesn't word wrap.
 */
function AutoResizeTextarea(_ref) {
  var _ref$className = _ref.className,
    className = _ref$className === void 0 ? '' : _ref$className,
    propsValue = _ref.value,
    onChange = _ref.onChange,
    _ref$spellCheck = _ref.spellCheck,
    spellCheck = _ref$spellCheck === void 0 ? false : _ref$spellCheck,
    _ref$placeholder = _ref.placeholder,
    placeholder = _ref$placeholder === void 0 ? '' : _ref$placeholder,
    _ref$disabled = _ref.disabled,
    disabled = _ref$disabled === void 0 ? false : _ref$disabled,
    _ref$delimiter = _ref.delimiter,
    delimiter = _ref$delimiter === void 0 ? '' : _ref$delimiter,
    _ref$id = _ref.id,
    id = _ref$id === void 0 ? '' : _ref$id,
    dataTestId = _ref['data-testid'];
  var _useState = useState(propsValue),
    _useState2 = _slicedToArray(_useState, 2),
    value = _useState2[0],
    setValue = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isPastedChange = _useState4[0],
    setIsPastedChange = _useState4[1];
  var element = useRef(null);
  var isFocused = useRef(false);
  var valueRef = useRef(value);
  valueRef.current = value;
  useEffect(function syncStateWithProp() {
    if (!isFocused.current || !delimiter) {
      // When not focused (or no delimiter), always sync to the new prop value.
      setValue(propsValue);
    } else if (implode(valueRef.current) !== implode(propsValue)) {
      // When focused with delimiter, only update if the imploded value changed to prevent clobbering delimiters
      setValue(explode(propsValue, delimiter));
    }
  }, [propsValue, delimiter]);
  function reCalculateLayout() {
    if (!element.current) {
      return;
    }
    element.current.style.height = '0'; // shrink component to get scrollHeight
    var resizedHeight = element.current.scrollHeight + (element.current.offsetHeight - element.current.clientHeight);
    // accounts for border, padding is captured by scroll height
    if (resizedHeight > 0) element.current.style.height = "".concat(resizedHeight, "px");
  }
  function handleChange(event) {
    var newValue = event.target.value;
    if (isPastedChange) {
      if (delimiter) newValue = explode(newValue, delimiter);
      setIsPastedChange(false);
    }
    setValue(newValue);
    // If there is a delimiter, the onChange value should always be the imploded version
    // to prevent mismatch when exiting without triggering onBlur
    // The exploded version is display only
    onChange(delimiter ? implode(newValue) : newValue);
  }
  function handleFocus() {
    if (!element.current) {
      return;
    }
    isFocused.current = true;
    if (delimiter) {
      setValue(explode(value, delimiter));
      reCalculateLayout();
    }
    element.current.scrollLeft = 0;
    // scroll left as it can be disorienting if you click on a long line
  }

  // make it explode when dragging the resize handle
  // by making it trigger focus (which normally doesn't
  // trigger when just resizing).
  function handleMouseDown() {
    if (!element.current) return;
    if (document.activeElement === element.current) return;
    element.current.focus();
  }
  function handleBlur() {
    isFocused.current = false;
    if (delimiter) {
      setValue(implode(value));
      onChange(implode(value));
    }
  }
  function handlePaste() {
    setIsPastedChange(true);
  }
  useEffect(function reCalculate() {
    reCalculateLayout();
  }, [value]);
  return /*#__PURE__*/_jsx("textarea", {
    ref: element,
    id: id,
    className: classNames(className, 'auto-resize-textarea form-control'),
    placeholder: placeholder,
    value: value,
    rows: 1,
    onChange: handleChange,
    onFocus: handleFocus,
    onMouseDown: handleMouseDown,
    onBlur: handleBlur,
    onPaste: handlePaste,
    spellCheck: spellCheck,
    disabled: disabled,
    "data-testid": dataTestId
  });
}
export default AutoResizeTextarea;
//# sourceMappingURL=AutoResizeTextarea.js.map